import { Checkbox, RadioButton } from "@fluidattacks/design";
import {
  createColumnHelper,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import type {
  ColumnDef,
  ColumnFiltersState,
  ColumnOrderState,
  OnChangeFn,
  PaginationState,
  Row,
  RowData,
  RowSelectionState,
  SortingState,
  TableOptions,
  VisibilityState,
} from "@tanstack/react-table";
import React, { useCallback, useMemo, useState } from "react";
import type { Dispatch, SetStateAction } from "react";

import type { IUseTable, IUseTableResult, TQueryHook } from "./types";

import { localStorage } from "storage";
import { useStoredState } from "storage/state";

const expandingModel = <T extends RowData>(
  expandRender: ((row: Row<T>) => React.ReactNode) | undefined,
  id: string,
): Partial<TableOptions<T>> => {
  return {
    enableExpanding: expandRender !== undefined,
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: (_row): boolean => expandRender !== undefined,
    meta: { id, onExpand: expandRender },
  };
};

const paginationModel = <T extends RowData>(
  mode: "server-side" | "client-side",
  data: Map<string, T>,
  total: number,
  paginate: (data: Map<string, T>, pagination: PaginationState) => T[],
  serverControl: [PaginationState, OnChangeFn<PaginationState>],
  clientControl: [PaginationState, Dispatch<SetStateAction<PaginationState>>],
): { data: T[] } & Partial<TableOptions<T>> => {
  const serverSide = mode === "server-side";

  return {
    data: serverSide ? paginate(data, serverControl[0]) : [...data.values()],
    getPaginationRowModel: serverSide ? undefined : getPaginationRowModel(),
    manualPagination: serverSide,
    onPaginationChange: serverSide ? serverControl[1] : clientControl[1],
    rowCount: serverSide ? total : undefined,
    state: { pagination: serverSide ? serverControl[0] : clientControl[0] },
  };
};

const sortingModel = <T extends RowData>(
  mode: "server-side" | "client-side",
  serverControl: [SortingState, OnChangeFn<SortingState>],
  clientControl: [SortingState, Dispatch<SetStateAction<SortingState>>],
): Partial<TableOptions<T>> => {
  const serverSide = mode === "server-side";

  return {
    /** Enables sorting icon for all the columns by default */
    enableSorting: true,
    getSortedRowModel: serverSide ? undefined : getSortedRowModel(),
    initialState: serverSide ? {} : { sorting: clientControl[0] },
    manualSorting: serverSide,
    onSortingChange: serverSide ? serverControl[1] : clientControl[1],
    state: {
      sorting: serverSide ? serverControl[0] : clientControl[0],
    },
  };
};

const filterModel = <T extends RowData>(
  mode: "server-side" | "client-side",
  clientControl: [
    ColumnFiltersState,
    Dispatch<SetStateAction<ColumnFiltersState>>,
  ],
): Partial<TableOptions<T>> => {
  const serverSide = mode === "server-side";

  return {
    getFilteredRowModel: serverSide ? undefined : getFilteredRowModel(),
    manualFiltering: serverSide,
    onColumnFiltersChange: serverSide ? undefined : clientControl[1],
    state: { columnFilters: serverSide ? undefined : clientControl[0] },
  };
};

const selectionModel = <T extends RowData, F extends object, S extends string>(
  rowOptions: IUseTable<T, F, S>["options"]["rowOptions"],
  rowSelection: RowSelectionState,
  setRowSelection: Dispatch<SetStateAction<RowSelectionState>>,
): Partial<TableOptions<T>> => {
  return {
    enableMultiRowSelection: rowOptions?.enableMultiSelection,
    enableRowSelection: rowOptions?.enableRowSelection,
    onRowClick: rowOptions?.onClick,
    onRowSelectionChange: setRowSelection,
    state: { rowSelection },
  };
};

const formatVisibilityState = (
  visible?: string[],
  hidden?: string[],
): VisibilityState => {
  return {
    ...(visible ?? []).reduce<VisibilityState>(
      (acc: VisibilityState, column: string): VisibilityState => {
        return {
          ...acc,
          [column]: true,
        };
      },
      {},
    ),
    ...(hidden ?? []).reduce<VisibilityState>(
      (acc: VisibilityState, column: string): VisibilityState => {
        return {
          ...acc,
          [column]: false,
        };
      },
      {},
    ),
  };
};

const columnModel = <T extends RowData, F extends object, S extends string>(
  id: string,
  columnOptions: IUseTable<T, F, S>["options"]["columnOptions"],
  rowOptions: IUseTable<T, F, S>["options"]["rowOptions"],
): Partial<TableOptions<T>> => {
  const storedOrder = localStorage.getItem(`${id}-orderState`);
  const storedVisibility = localStorage.getItem(`${id}-visibilityState`);

  const selectable = rowOptions?.enableMultiSelection ?? false;

  const columnPinning = {
    left: [
      ...(selectable ? ["selection"] : []),
      ...(columnOptions?.pinned ?? []),
    ],
  };

  const columnOrder =
    storedOrder === null || typeof storedOrder !== "string"
      ? columnOptions?.order
      : (JSON.parse(storedOrder) as ColumnOrderState);

  const columnVisibility =
    storedVisibility === null || typeof storedVisibility !== "string"
      ? formatVisibilityState(columnOptions?.visible, columnOptions?.hidden)
      : (JSON.parse(storedVisibility) as VisibilityState);

  return {
    columnResizeMode: "onChange",
    defaultColumn: { maxSize: 3000, minSize: 100 },
    enableColumnResizing: true,
    meta: { defaultColumnOrder: columnOptions?.order, id },
    state: {
      columnOrder,
      columnPinning,
      columnVisibility,
    },
  };
};

const DEFAULT_PAGINATION: PaginationState = { pageIndex: 0, pageSize: 10 };

const isDefaultPagination = (pagination: PaginationState): boolean => {
  return (
    pagination.pageIndex === DEFAULT_PAGINATION.pageIndex &&
    pagination.pageSize === DEFAULT_PAGINATION.pageSize
  );
};

const usePaginationState = (
  id: string,
  defaultPagination: PaginationState = DEFAULT_PAGINATION,
): [PaginationState, Dispatch<SetStateAction<PaginationState>>] => {
  const storageKey = `${id}-pagination`;

  // Load initial state from localStorage or use default
  const [pagination, setPagination] = useState<PaginationState>(
    (): PaginationState => {
      const stored = localStorage.getItem(storageKey);
      if (stored === null || typeof stored !== "string") {
        return defaultPagination;
      }

      try {
        const parsed = JSON.parse(stored) as PaginationState;

        return parsed;
      } catch {
        return defaultPagination;
      }
    },
  );

  const setPaginationWithStorage = useCallback(
    (newValue: SetStateAction<PaginationState>): void => {
      const nextValue =
        newValue instanceof Function ? newValue(pagination) : newValue;

      // Only store if the value is different from default
      if (isDefaultPagination(nextValue)) {
        localStorage.removeItem(storageKey);
      } else {
        void localStorage.setItem(storageKey, JSON.stringify(nextValue));
      }

      setPagination(nextValue);
    },
    [pagination, storageKey],
  );

  return [pagination, setPaginationWithStorage];
};

/**
 * ### useTable
 *
 * Hook to create a server-side table with pagination, sorting, and filtering.
 *
 * @template T - The type of the data to show in the table
 * @template F - The filters type
 * @template S - The sort enum
 */
const useTable = <T, F extends object, S extends string>({
  data,
  columns,
  id,
  total,
  filters,
  fetchMore,
  options,
}: IUseTable<T, F, S>): IUseTableResult<T> => {
  const {
    actionsOptions = {},
    rowOptions = {},
    columnOptions = {},
    filtersMode = "server-side",
    paginationMode = "server-side",
    loading = false,
    tooltips = [],
  } = options;
  const { onSearch, searchPlaceholder, search } = actionsOptions;

  const {
    getId = (item: T): string => (item as unknown as { id: string }).id,
    enableMultiSelection = false,
    enableRowSelection = false,
    expandRender = undefined,
  } = rowOptions;

  const newColumns = useMemo((): ColumnDef<T, string>[] => {
    const multiSelectionColumn = createColumnHelper<T>().display({
      cell: ({ row }): React.ReactNode =>
        React.createElement(Checkbox, {
          checked: row.getIsSelected(),
          disabled: !row.getCanSelect(),
          name: `select-${row.id}`,
          onChange: row.getToggleSelectedHandler(),
        }),
      enableResizing: false,
      enableSorting: false,
      header: ({ table }): React.ReactNode =>
        React.createElement(Checkbox, {
          checked: table.getIsAllPageRowsSelected(),
          name: "select-all",
          onChange: table.getToggleAllPageRowsSelectedHandler(),
        }),
      id: "selection",
      maxSize: 18,
      minSize: 18,
      size: 18,
    });
    const singleSelectionColumn = createColumnHelper<T>().display({
      cell: ({ row }): React.ReactNode =>
        React.createElement(RadioButton, {
          checked: row.getIsSelected(),
          disabled: !row.getCanSelect(),
          name: `select-${row.id}`,
          onChange: row.getToggleSelectedHandler(),
        }),
      enableResizing: false,
      enableSorting: false,
      header: (): React.ReactNode => undefined,
      id: "selection",
      maxSize: 18,
      minSize: 18,
      size: 18,
    });

    return [
      ...(enableMultiSelection ? [multiSelectionColumn] : []),
      ...(enableRowSelection ? [singleSelectionColumn] : []),
      ...columns,
    ];
  }, [columns, enableMultiSelection, enableRowSelection]);

  const mappedData = useMemo(
    (): Map<string, T> =>
      new Map(data.map((item): [string, T] => [getId(item), item])),
    [data, getId],
  );

  const [clientFilter, setClientFilter] = useState<ColumnFiltersState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [sorting, setSorting] = useState<SortingState>(
    columnOptions.sorting ?? [],
  );
  const [clientPagination, setClientPagination] = usePaginationState(id);
  const [, setColumnOrder] = useStoredState<ColumnOrderState>(
    `${id}-orderState`,
    columnOptions.order ?? [],
    "local",
  );
  const [, setColumnVisibility] = useStoredState<VisibilityState>(
    `${id}-visibilityState`,
    formatVisibilityState(columnOptions.visible, columnOptions.hidden),
    "local",
  );

  const handlePagination = useCallback(
    (updater: (old: PaginationState) => PaginationState): void => {
      const movePage = async (): Promise<void> => {
        const MIN_PAGE_SIZE = 10;
        const page = {
          pageIndex: filters?.pagination?.pageIndex ?? 0,
          pageSize: filters?.pagination?.pageSize ?? MIN_PAGE_SIZE,
        };
        const { pageIndex, pageSize } = updater(page);
        const pageSizeChanged = page.pageSize !== pageSize;

        if (
          mappedData.size !== total &&
          mappedData.size < (pageIndex + 1) * pageSize &&
          !pageSizeChanged &&
          fetchMore
        ) {
          await fetchMore();
        }

        if (pageSizeChanged) {
          filters?.setPageIndex?.(0);
          filters?.setPageSize?.(pageSize);
        } else {
          filters?.setPageIndex?.(pageIndex);
          filters?.setPageSize?.(pageSize);
        }
      };

      void movePage();
    },
    [filters, fetchMore, mappedData.size, total],
  );

  const handleSorting = useCallback(
    (updater: (old: SortingState) => SortingState): void => {
      const newSorting = updater(sorting);
      if (newSorting.length > 0) {
        const field = newSorting[0].id.toUpperCase() as `${S}`;
        const order = newSorting[0].desc ? "DESC" : "ASC";
        filters?.setSortBy?.(field, order);
      } else {
        filters?.resetSortBy?.();
      }
      setSorting(newSorting);
    },
    [sorting, filters],
  );

  const paginate = useCallback(
    (fullData: Map<string, T>, fullPagination: PaginationState): T[] => {
      const { pageIndex: index, pageSize: items } = fullPagination;

      return [...fullData.values()].slice(index * items, (index + 1) * items);
    },
    [],
  );

  const table = useReactTable<T>({
    ...expandingModel(expandRender, id),
    ...selectionModel(rowOptions, rowSelection, setRowSelection),
    ...columnModel(id, columnOptions, rowOptions),
    ...paginationModel(
      paginationMode,
      mappedData,
      total,
      paginate,
      [
        filters?.pagination as PaginationState,
        handlePagination as OnChangeFn<PaginationState>,
      ],
      [clientPagination, setClientPagination],
    ),
    ...filterModel(filtersMode, [clientFilter, setClientFilter]),
    ...sortingModel(
      paginationMode,
      [sorting, handleSorting as OnChangeFn<SortingState>],
      [sorting, setSorting],
    ),
    autoResetAll: false,
    columns: newColumns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: getId,
    initialState: { columnOrder: columnOptions.order },
    meta: {
      ...expandingModel(expandRender, id).meta,
      ...columnModel(id, columnOptions, rowOptions).meta,
      data: mappedData,
      filters: filters as unknown as Record<string, string>,
      groupHeaders: columnOptions.groupHeaders,
      id,
      loading,
      onSearch,
      search,
      searchPlaceholder,
      tooltips,
    },
    onColumnOrderChange: setColumnOrder,
    onColumnVisibilityChange: setColumnVisibility,
    state: {
      ...selectionModel(rowOptions, rowSelection, setRowSelection).state,
      ...columnModel(id, columnOptions, rowOptions).state,
      ...filterModel(filtersMode, [clientFilter, setClientFilter]).state,
      ...paginationModel(
        paginationMode,
        mappedData,
        total,
        paginate,
        [
          filters?.pagination as PaginationState,
          handlePagination as OnChangeFn<PaginationState>,
        ],
        [clientPagination, setClientPagination],
      ).state,
      ...sortingModel(
        paginationMode,
        [sorting, handleSorting as OnChangeFn<SortingState>],
        [sorting, setSorting],
      ).state,
    },
  });

  return { table };
};

export { useTable };
export type { TQueryHook };
